// src/hooks/useLocalStorage.js
//
// Thin wrapper around useState that reads the initial value from localStorage
// and keeps it in sync on every write.
//
// Usage:
//   const [showCompleted, setShowCompleted] = useLocalStorage("ripple:showCompleted", false);
//
// The setter accepts a value or a functional updater, just like useState.
// Silently degrades to plain useState when localStorage is unavailable.
//
// Key-change behaviour: when the `key` prop changes (e.g. because a parent
// switches to a per-market key) the hook re-reads from localStorage so the
// new key's stored value is reflected immediately.

import { useState, useCallback, useEffect, useRef } from "react";

/**
 * @template T
 * @param {string}  key           localStorage key
 * @param {T}       defaultValue  Value used when the key is absent or unparseable
 * @returns {[T, (next: T | ((prev: T) => T)) => void]}
 */
export function useLocalStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  // Keep a ref to the latest defaultValue so the key-change effect below can
  // use it without becoming a dependency (avoids re-running on every render).
  const defaultRef = useRef(defaultValue);
  defaultRef.current = defaultValue;

  // Re-sync state when the key changes (e.g. market switch rotates to a
  // per-market key).  The initial useState call only runs once, so without
  // this effect a key change would leave stale state in place.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(key);
      setValue(stored !== null ? JSON.parse(stored) : defaultRef.current);
    } catch {
      setValue(defaultRef.current);
    }
  }, [key]); // deliberately omit defaultRef — it is stable via the ref

  const setAndPersist = useCallback(
    (nextOrUpdater) => {
      setValue((prev) => {
        const next =
          typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // localStorage may be unavailable (private browsing, quota exceeded, etc.)
        }
        return next;
      });
    },
    [key],
  );

  return [value, setAndPersist];
}
